import React, { useState, useEffect } from "react";
import { Form, Input, Button, Typography, ConfigProvider, App } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { getInviteLink, registerViaLink } from "../../api/invite_link_endpoints";
import { MODULE_SCOPES } from "../../constants/moduleScopes";
import { PasswordRequirements } from "../../components/auth/PasswordRequirements";
import { usePasswordPolicy } from "../../hooks/usePasswordPolicy";
import LoginImage from "../../assets/login_image.webp";

const { Title, Paragraph } = Typography;

const JoinPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { message } = App.useApp();

  const [step, setStep] = useState("loading"); // loading | form | success | invalid
  const [linkInfo, setLinkInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [passwordValue, setPasswordValue] = useState("");
  const [invalidReason, setInvalidReason] = useState("");
  const { rules: passwordRules, formRules: passwordFormRules } =
    usePasswordPolicy();

  useEffect(() => {
    getInviteLink(token)
      .then((info) => {
        if (!info.is_valid) {
          const reasons = {
            expired: "This invite link has expired.",
            exhausted: "This invite link has reached its maximum uses.",
            revoked: "This invite link has been revoked.",
            not_found: "This invite link does not exist.",
          };
          setInvalidReason(reasons[info.error] || "This invite link is no longer valid.");
          setStep("invalid");
        } else {
          setLinkInfo(info);
          setStep("form");
        }
      })
      .catch(() => {
        setInvalidReason("Could not load invite link. Please check the URL and try again.");
        setStep("invalid");
      });
  }, [token]);

  const onFinish = async ({ name, email, password }) => {
    setLoading(true);
    try {
      await registerViaLink(token, { name, email, password });
      setStep("success");
    } catch (err) {
      message.error({ content: err.message, duration: 5 });
    } finally {
      setLoading(false);
    }
  };

  const roleLabel = {
    super_admin: "Super Admin",
    admin: "Admin",
    normal_user: "User",
  };

  return (
    <div className="flex justify-center items-center h-[100vh] w-[100vw] relative bg-white">
      <div className="absolute top-[1.625rem] left-[1.625rem] text-[#1D2939] text-[1rem] font-normal tracking-[1.2px]">
        Dash<span className="text-[1.25rem] font-semibold tracking-normal">ONE</span>
      </div>

      <div className="flex justify-center items-center flex-1 flex-col gap-[2rem] px-4">
        <div className="w-full max-w-[31.625rem] flex flex-col">

          {/* Loading */}
          {step === "loading" && (
            <div className="flex justify-center py-16 text-gray-400 text-sm">Verifying invite link…</div>
          )}

          {/* Invalid link */}
          {step === "invalid" && (
            <div className="flex flex-col gap-[48px]">
              <Title level={2} className="!m-0 !text-[30px] !font-bold !text-[rgba(0,0,0,0.85)]">
                Invalid link
              </Title>
              <div className="bg-[#fff2f0] border border-[#ffccc7] rounded-[24px] px-[18px] py-[16px]">
                <Paragraph className="!m-0 !text-[15px] !text-[rgba(0,0,0,0.75)]">
                  {invalidReason}
                </Paragraph>
              </div>
              <Button
                type="primary"
                className="!rounded-[24px] !text-[16px] !font-normal !h-[36px]"
                onClick={() => navigate("/login")}
              >
                Go to Log in
              </Button>
            </div>
          )}

          {/* Registration form */}
          {step === "form" && (
            <div className="w-full flex flex-col gap-[48px]">
              <div className="flex flex-col gap-2">
                <Title level={2} className="!m-0 !text-[30px] !font-bold !text-[rgba(0,0,0,0.85)]">
                  Create your account
                </Title>
                {linkInfo && (
                  <div className="flex flex-col gap-2">
                    <p className="text-sm text-gray-500 m-0">
                      You've been invited as <span className="font-medium text-gray-700">{roleLabel[linkInfo.role] ?? linkInfo.role}</span>
                      {" "}to workspace <span className="font-medium text-gray-700">{linkInfo.id_client.toUpperCase().replace(/_/g, " ")}</span>.
                    </p>
                    {linkInfo.product_scopes && Object.keys(linkInfo.product_scopes).length > 0 && (
                      <div className="flex flex-col gap-1">
                        {MODULE_SCOPES.map((module) => {
                          const countries = linkInfo.product_scopes[module.key];
                          if (!countries || countries.length === 0) return null;
                          return (
                            <p key={module.key} className="text-sm text-gray-500 m-0">
                              <span className="font-medium text-gray-700">{module.label}:</span>{" "}
                              {countries.join(", ")}
                            </p>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <ConfigProvider theme={{ token: { colorPrimary: "#0E49B5", borderRadius: 24 } }}>
                <Form
                  form={form}
                  onFinish={onFinish}
                  autoComplete="off"
                  layout="vertical"
                  className="w-full flex flex-col gap-[20px]"
                >
                  <Form.Item
                    label={
                      <span className="text-[14px] font-medium text-[#3f3f46] px-[6px]">
                        Full Name <span className="text-gray-400 font-normal">(optional)</span>
                      </span>
                    }
                    name="name"
                    className="!mb-0"
                  >
                    <Input
                      placeholder="Your full name"
                      className="!rounded-[24px] !px-4 !py-[6px]"
                      style={{ fontSize: "14px", fontFamily: "Inter" }}
                    />
                  </Form.Item>

                  <Form.Item
                    label={<span className="text-[14px] font-medium text-[#3f3f46] px-[6px]">Email</span>}
                    name="email"
                    rules={[
                      { required: true, message: "Please enter your email" },
                      { type: "email", message: "Please enter a valid email" },
                    ]}
                    className="!mb-0"
                  >
                    <Input
                      placeholder="your@company.com"
                      className="!rounded-[24px] !px-4 !py-[6px]"
                      style={{ fontSize: "14px", fontFamily: "Inter" }}
                    />
                  </Form.Item>

                  <Form.Item
                    label={<span className="text-[14px] font-medium text-[#3f3f46] px-[6px]">Password</span>}
                    name="password"
                    rules={[
                      { required: true, message: "Please enter a password" },
                      ...passwordFormRules,
                    ]}
                    className="!mb-0"
                  >
                    <Input.Password
                      placeholder="Password"
                      className="!rounded-[24px] !px-4 !py-[6px]"
                      style={{ fontSize: "14px", fontFamily: "Inter" }}
                      onChange={(e) => setPasswordValue(e.target.value)}
                    />
                  </Form.Item>
                  <PasswordRequirements
                    password={passwordValue}
                    rules={passwordRules}
                  />

                  <Form.Item
                    label={<span className="text-[14px] font-medium text-[#3f3f46] px-[6px]">Confirm Password</span>}
                    name="confirmPassword"
                    dependencies={["password"]}
                    rules={[
                      { required: true, message: "Please confirm your password" },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue("password") === value) return Promise.resolve();
                          return Promise.reject(new Error("Passwords do not match"));
                        },
                      }),
                    ]}
                    className="!mb-0"
                  >
                    <Input.Password
                      placeholder="Confirm Password"
                      className="!rounded-[24px] !px-4 !py-[6px]"
                      style={{ fontSize: "14px", fontFamily: "Inter" }}
                    />
                  </Form.Item>
                </Form>
              </ConfigProvider>

              <Button
                type="primary"
                loading={loading}
                onClick={() => form.submit()}
                className="!rounded-[24px] !text-[16px] !font-normal !h-[36px]"
                block
              >
                Create Account
              </Button>
            </div>
          )}

          {/* Success */}
          {step === "success" && (
            <div className="w-full flex flex-col gap-[48px]">
              <Title level={2} className="!m-0 !text-[30px] !font-bold !text-[rgba(0,0,0,0.85)]">
                Account created!
              </Title>
              <div className="bg-[#f6ffed] border border-[#389e0d] rounded-[24px] px-[18px] py-[16px]">
                <Paragraph className="!m-0 !text-[15px] !text-[rgba(0,0,0,0.75)]">
                  Your account has been created. You can now log in with your email and password.
                </Paragraph>
              </div>
              <Button
                type="primary"
                className="!rounded-[24px] !text-[16px] !font-normal !h-[36px]"
                onClick={() => navigate("/login")}
                block
              >
                Go to Log in
              </Button>
            </div>
          )}

        </div>
      </div>

      <div className="absolute bottom-[2rem] left-[1.625rem] text-[rgb(71,84,103)] text-[0.75rem]">
        @DashONE 2025
      </div>
      <div
        className="flex-1 h-full w-[50vw] self-stretch rounded-[20px_0_0_20px] bg-no-repeat bg-center bg-cover hidden md:block"
        style={{ backgroundImage: `url(${LoginImage})` }}
      />
    </div>
  );
};

export default JoinPage;
